/**
 * החברות שלי — the Tenant Workspace landing screen. 09 §5.1.
 *
 * This is a PARENT SCREEN (a list of rows of one entity kind), not a context selector. The context
 * SELECTOR is the dropdown in the context bar and there is exactly one of those. This screen exists
 * because "my companies" is also a place you go to look at things — it has search, it shows
 * lifecycle state per row, and it is where a denial's "חזרה לחברות שלי" lands.
 *
 * Binding rules from §5.1 implemented here:
 *   • Every row shows an EXPLICIT lifecycle state. Never inferred, never hidden on an inferred default.
 *   • A broken-identity row still appears, marked `דורש טיפול` — never omitted.
 *   • Search is on legal name and ח.פ. NEVER on a db code: a customer does not know their db code.
 *   • The list is the server's single "visible companies" answer. Nothing is filtered client-side
 *     for permission reasons — the picker never shows a company and then denies on click.
 */

import { useMemo, useState } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { LifecycleChip } from '../components/LifecycleChip';
import type { Column } from '../components/DataTable';
import type { CompanySummary } from '../api/contract';
import { routes } from '../routes';

export function CompanyPickerScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim();
    if (q === '') return ctx.visibleCompanies;
    // Display-time search only. This narrows what is on screen; it never decides access.
    return ctx.visibleCompanies.filter(
      (c) => c.legalName.includes(q) || (c.registrationNumber ?? '').includes(q),
    );
  }, [ctx.visibleCompanies, query]);

  const columns: readonly Column<CompanySummary>[] = [
    {
      key: 'name',
      headerHe: 'שם החברה',
      render: (c) => (
        <span className="company-cell">
          <span className="company-cell__name">{c.legalName}</span>
          {/* 09 §5.1: ח.פ as a muted second line. Never a code as the identity. */}
          <span className="company-cell__id" dir="ltr">
            {c.registrationNumber ?? 'ללא ח.פ רשום'}
          </span>
        </span>
      ),
    },
    {
      key: 'lifecycle',
      headerHe: 'מצב החברה',
      render: (c) => <LifecycleChip status={c.lifecycleStatus} />,
    },
    {
      key: 'attention',
      headerHe: 'הערות',
      render: (c) =>
        c.needsAttention ? <span className="chip chip--attention">דורש טיפול</span> : null,
    },
  ];

  return (
    <AppShell titleHe="החברות שלי">
      {/*
        The office's own administration is a place, not a settings gear. OA-0001 §3.2 hands the
        customer the authorization system to OPERATE, and a system reachable only from a menu nobody
        opens is one the customer does not know they have.

        This is an affordance and never a control: a user the gate would refuse still sees the link
        and still gets a written Hebrew denial rather than a missing button and no explanation.
      */}
      <div className="actions-row">
        <a className="btn" href={routes.tenantAdmin}>
          ניהול המשרד
        </a>
      </div>

      <label className="field field--inline">
        <span className="field__label">חיפוש חברה</span>
        <input
          className="field__input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder="שם חברה או ח.פ"
        />
      </label>

      {/*
        THE READ, NOT THE LIST. **Phase 2 §6, vertical A.**

        This screen is the one place whose entire subject is *"which companies may I open"*, so it is
        the one place where an empty table and an unanswered read must not look alike. A user who
        opens GL-POINT and is told they have access to no companies — when in fact the read was
        refused or the server was unreachable — telephones the office to ask why their access was
        removed, and nobody removed anything.

        The read happens in `<App>`, because the context bar names the current company on every
        screen and 04 I6 allows the working context exactly one source. What arrives here is the
        RESULT of that one read.
      */}
      <ServerRead result={ctx.companiesResult} whatHe="את החברות שלך" skeletonRows={8}>
        {() => (
          <DataTable<CompanySummary>
            captionHe="החברות שלי"
            columns={columns}
            rows={rows}
            rowKey={(c) => c.companyUid}
            childHref={(c) => routes.companyHome(c.companyUid)}
            filterSummaryHe={
              query.trim() === ''
                ? // 09 §5.1 "313 is not 313": any "N חברות" figure states which N it is.
                  `מציג ${ctx.visibleCompanies.length} חברות שיש לך גישה אליהן`
                : `מציג ${rows.length} מתוך ${ctx.visibleCompanies.length} חברות · סנן: "${query.trim()}"`
            }
            // Two different empties, and they are two different sentences. This one is about the
            // SEARCH; an office with no companies at all is the one below it.
            emptyStateHe={
              ctx.visibleCompanies.length === 0
                ? 'אין חברות שיש לך גישה אליהן. גישה לחברה היא הענקה מפורשת, ומנהל המשרד מעניק אותה במסך הרשאות לחברות.'
                : 'לא נמצאו חברות התואמות לחיפוש.'
            }
          />
        )}
      </ServerRead>
    </AppShell>
  );
}
